import SVG from 'svg.js';
import {AssetGroup} from '@deviceinsight/ng-ui-api-client';

import {paginationSettingsAll} from '../../../../api/searchAssets';
import {ELEMENT_CLASSNAME} from '../utils/consts';
import {loadGroupDevices} from '../utils/FloorplanWidgetUtils';
import {addSvgDeviceHandlers, addUnknownDeviceHandlers, setAlertMarkers} from './FloorplanWidgetDeviceUtils';
import type {AssetWithMetaData, StateAccess} from '../types';
import {getAssetDeviceType} from '../../../ruleengine/ruleEditHelpers';
import {WidgetSvg} from '../hooks/types';
import {DashboardWidgetConfig} from '@deviceinsight/ng-ui-scale-lib/api';

const reLinkFloorplanSvg = (
	floorplanSvg: string | null | undefined,
	updateSvg: () => void,
	uniqueSvgKey: string | null | undefined,
	assetGroup: AssetGroup | null | undefined,
	stateAccessParams: StateAccess
) => {
	const {setSelectedDevice, setAvailableDevices, setSvgRootObj} = stateAccessParams;

	if (floorplanSvg) {
		const svgNodeParser = new DOMParser();
		const svgNodeDom = svgNodeParser.parseFromString(floorplanSvg, 'image/svg+xml'); // application/xml

		if (svgNodeDom.hasChildNodes()) {
			const svgNode: SVGElement = svgNodeDom.childNodes[0] as SVGElement;

			if (svgNode && svgNode.innerHTML) {
				// @ts-expect-error setAttributes does exist
				svgNode.setAttribute('id', uniqueSvgKey);
				const container = document.getElementById('floorplanContainer');

				if (container) {
					if (container.childNodes.length) {
						container.removeChild(container.childNodes[0]);
					}
					const innerHTML = svgNode.innerHTML;
					container.appendChild(svgNode);
					// Support Ticket 220152: Firefox mangles <?xpacket ...?> tags during appendChild
					svgNode.innerHTML = innerHTML;

					const svgRootObj = SVG(uniqueSvgKey ?? 'test') as unknown as WidgetSvg;
					const allDeviceElementsInSvg = svgRootObj.select(`g.${ELEMENT_CLASSNAME}`);
					const assetIds: number[] = [];
					allDeviceElementsInSvg?.members.forEach((member) => {
						const assetId = member.attr('id').replace('added_device_', '');
						assetIds.push(parseInt(assetId));
					});
					loadGroupDevices('', assetGroup, [], paginationSettingsAll, assetIds).then(({content: assets}) => {
						const allGroupDevices: AssetWithMetaData[] = assets.map((asset) => {
							return {
								asset,
								meta: {
									type: getAssetDeviceType(asset)
								}
							};
						});
						// verify that all devices placed on floorplan are assigned to this group, if not display alert info / handle device differently
						const alertingIds: number[] = [];
						assetIds.forEach((idFromSvg) => {
							const assetInGroup = assets.find((asset) => asset.id === idFromSvg);

							if (!assetInGroup) {
								console.error(
									`Asset with id ${idFromSvg} is not a member of this group anymore and cannot be handled correctly. Please remove it from the floorplan`
								);
								const svgId = `added_device_${idFromSvg}`;
								const svgDeviceObj = svgRootObj.select(`g#${svgId}`);

								if (svgDeviceObj && svgDeviceObj.members.length) {
									addUnknownDeviceHandlers(svgDeviceObj, svgRootObj, updateSvg, setSelectedDevice);
								}

								alertingIds.push(idFromSvg);
							}
						});
						setAlertMarkers(svgRootObj, alertingIds);
						// set handlers for devices in the group
						const devicesAlreadyAdded: AssetWithMetaData[] = [];
						allGroupDevices.forEach((device) => {
							const {
								asset: {id}
							} = device;
							const svgId = `added_device_${id}`;
							const svgDeviceObj = svgRootObj.select(`g#${svgId}`);

							if (svgDeviceObj && svgDeviceObj.members.length) {
								addSvgDeviceHandlers(svgDeviceObj, svgRootObj, device, updateSvg, setSelectedDevice);
								devicesAlreadyAdded.push(device);
							}
						});
						setAvailableDevices(devicesAlreadyAdded);
					});
					setSvgRootObj(svgRootObj);
				}
			}
		}
	}
};

const removeFloorplanImage = (
	config: DashboardWidgetConfig,
	onChangeConfig: (arg0: DashboardWidgetConfig) => void,
	stateAccessParams: StateAccess
) => {
	const {settings} = config;
	const {setSvgRootObj, setAvailableDevices} = stateAccessParams;
	const container = document.getElementById('floorplanContainer');

	if (container && container.childNodes.length) {
		container.removeChild(container.childNodes[0]);
	}

	setSvgRootObj(undefined);
	setAvailableDevices([]);
	onChangeConfig({...config, settings: {...settings, floorplanSvg: undefined}});
};

export {reLinkFloorplanSvg, removeFloorplanImage};
